/**
 * DocumentSections - Composant partagé pour l'affichage des documents dans les modals admin
 * Utilisé par CooperMembresManagement.js et MemberModals.js
 */
import React, { useState, useMemo } from 'react';

import { Button } from './ui/button';

import { Eye, FileText, Clock, Download, Shield, Award, FileCheck, Loader2, Trash2, X, CheckCircle } from 'lucide-react';
import { downloadDocument, viewDocument } from '../services/downloadService';
import { toast } from 'sonner';

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// --- Helpers ---

import EditChantierInfoDialog from './DocumentSections/EditChantierInfoDialog';
import IntervenantsManagerDialog from './DocumentSections/IntervenantsManagerDialog';
import PropositionsESSSection from './DocumentSections/PropositionsESSSection';
import SimulationsSection from './DocumentSections/SimulationsSection';
import FtpeSection from './DocumentSections/FtpeSection';
import GedSection from './DocumentSections/GedSection';
import AdminDocsSection from './DocumentSections/AdminDocsSection';
import ArchivedDocumentsSection from './DocumentSections/ArchivedDocumentsSection';
import DispositifsMembreSection from './DocumentSections/DispositifsMembreSection';
import { PROPOSITION_TYPES, getFileIcon, formatFileSize, getCategorieLabel } from './DocumentSections/helpers';
import { RegenererRecuButton } from './DocumentSections/RegenererRecuButton';
import { DispositifBadge } from './DispositifBadge';
import { SoldesDusSection } from './SoldeDu';

// --- Section générique de documents ---
function GenericDocSection({ title, icon: Icon, iconColor, items, bgColor, borderColor, textColor, membreId, onRefresh }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconColor}`} />{title} ({items.length})
      </h4>
      <div className="space-y-2">
        {items.map((item) => {
          const canView = item.url || item.adhesion_id || item.souscription_id || item.can_download;
          return (
            <div key={item.id} className={`flex items-center justify-between p-3 ${bgColor} rounded-lg border ${borderColor}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm"><Icon className={`h-4 w-4 ${iconColor}`} /></div>
                <div>
                  <p className="font-medium text-gray-900 text-sm flex items-center gap-2">{item.nom || item.titre || item.type || title}<DispositifBadge code={item.dispositif} testId={`dispositif-badge-${item.id}`} /></p>
                  <p className="text-xs text-gray-500">
                    {item.date_creation && `Créé le ${new Date(item.date_creation).toLocaleDateString('fr-FR')}`}
                    {(item.date_expiration || item.date_fin) && (
                      <span className={`ml-2 font-medium ${textColor}`}>
                        • Échéance : {new Date(item.date_expiration || item.date_fin).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </p>
                  {item.statut_label && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid={`doc-statut-${item.id}`}>{item.statut_label}</span>
                  )}
                  {item.document_status && !item.statut_label && (
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                      item.document_status === 'signee' ? 'bg-green-100 text-green-700' :
                      item.document_status === 'non_signee' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {item.document_status === 'signee' ? 'Signée' : item.document_status === 'non_signee' ? 'Non signée' : 'En cours'}
                    </span>
                  )}
                </div>
              </div>
              {canView && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className={`${borderColor} ${textColor}`} onClick={() => viewDocument(item)}>
                    <Eye className="h-4 w-4 mr-1" />Voir
                  </Button>
                  <Button variant="outline" size="sm" className={`${borderColor} ${textColor}`} onClick={() => downloadDocument(item)}>
                    <Download className="h-4 w-4 mr-1" />Télécharger
                  </Button>
                  <RegenererRecuButton doc={item} membreId={membreId} onDone={onRefresh} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Composant principal: toutes les sections de documents ---
export default function DocumentSections({ membreDocuments, membreId, onResume, onRefresh }) {
  // État de sélection partagé pour suppression unitaire / en masse
  // des Simulations et Propositions ESS (clé = "type:id").
  const [selection, setSelection] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // État du dialog "Compléter les infos chantier" (DOMIUM uniquement).
  const [editChantierSim, setEditChantierSim] = useState(null);
  // État du dialog "Intervenants" (DOMIUM uniquement).
  const [manageIntervenantsSim, setManageIntervenantsSim] = useState(null);

  const handleEditChantierInfo = (sim) => setEditChantierSim(sim);
  const handleManageIntervenants = (sim) => setManageIntervenantsSim(sim);

  const handleToggle = (item) => {
    const key = `${item.type}:${item.id}`;
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Index des items sélectionnés (clé → item complet) pour la suppression bulk.
  const selectedItemsMap = useMemo(() => {
    const map = new Map();
    if (!membreDocuments) return map;
    const sims = membreDocuments.simulations_en_cours || [];
    const props = (membreDocuments.documents_contractuels || []).filter(
      (d) => PROPOSITION_TYPES.has(d.type)
    );
    sims.forEach((s) => {
      const k = `simulation:${s.id}`;
      if (selection.has(k)) map.set(k, { type: 'simulation', id: s.id, label: s.nom || s.type || 'Simulation' });
    });
    props.forEach((p) => {
      const k = `proposition_ess:${p.id}`;
      if (selection.has(k)) map.set(k, { type: 'proposition_ess', id: p.id, label: p.nom || `Proposition ${p.id}` });
    });
    return map;
  }, [selection, membreDocuments]);

  const clearSelection = () => setSelection(new Set());

  const handleDeleteOne = async (item) => {
    if (!membreId) {
      toast.error('Membre inconnu — suppression impossible');
      return;
    }
    const labelType = item.type === 'simulation' ? 'simulation' : 'proposition ESS';
    if (!window.confirm(`Supprimer définitivement cette ${labelType} ?\n\n${item.label}\n\nCette action est irréversible.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const path = item.type === 'simulation' ? 'simulation' : 'proposition-ess';
      await axios.delete(`${BACKEND_URL}/api/cooper/membres/${membreId}/${path}/${encodeURIComponent(item.id)}`, { headers });
      toast.success(`${labelType.charAt(0).toUpperCase() + labelType.slice(1)} supprimée`);
      setSelection((prev) => {
        const next = new Set(prev);
        next.delete(`${item.type}:${item.id}`);
        return next;
      });
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erreur lors de la suppression de la ${labelType}`);
    }
  };

  const handleBulkDelete = async () => {
    if (!membreId || selection.size === 0) return;
    const items = Array.from(selectedItemsMap.values()).map((it) => ({ type: it.type, id: it.id }));
    const nb = items.length;
    if (!window.confirm(`Supprimer définitivement ${nb} élément${nb > 1 ? 's' : ''} sélectionné${nb > 1 ? 's' : ''} ?\n\nCette action est irréversible.`)) {
      return;
    }
    setBulkDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${BACKEND_URL}/api/cooper/membres/${membreId}/documents/bulk-delete`,
        { items },
        { headers }
      );
      const { total_deleted, errors } = res.data || {};
      if (total_deleted > 0) {
        toast.success(`${total_deleted} élément${total_deleted > 1 ? 's' : ''} supprimé${total_deleted > 1 ? 's' : ''}`);
      }
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} élément${errors.length > 1 ? 's' : ''} non supprimé${errors.length > 1 ? 's' : ''} : ${errors[0].error}`);
      }
      clearSelection();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression en masse');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Détermine l'endpoint API à appeler selon le type de simulation.
  // DOMIUM        → /api/domium/devis/{ref}
  // BATICO/RCDS   → /api/batico/proposition/{id}
  const getSignatureBaseUrl = (sim) => {
    const isDomium = sim.type === 'simulation_domium' || sim.source === 'domium_subscriptions';
    const ref = encodeURIComponent(sim.reference || sim.id);
    return isDomium
      ? `${BACKEND_URL}/api/domium/devis/${ref}`
      : `${BACKEND_URL}/api/batico/proposition/${ref}`;
  };

  // Téléverser une signature manuscrite pour une simulation DOMIUM ou BATICO.
  // Ouvre un input file natif (pas besoin de modale dédiée), envoie au backend
  // et rafraîchit la liste pour basculer le badge "Devis signé".
  const handleUploadDomiumSignature = (sim) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/png,image/jpeg,image/jpg';
    input.onchange = async (e) => {
      const file = e.target?.files?.[0];
      if (!file) return;
      // Limite UI : 10 Mo (validation backend identique)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10 Mo).');
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(
          `${getSignatureBaseUrl(sim)}/upload-manual-signature`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        toast.success(res.data?.message || 'Signature téléversée. Le document valant facture est désormais disponible.');
        if (onRefresh) onRefresh();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur lors du téléversement');
      }
    };
    input.click();
  };

  const handleDownloadDocumentFacture = async (sim) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${getSignatureBaseUrl(sim)}/document-facture`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      // Forcer le download
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Document_Valant_Facture_${sim.reference || sim.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Document valant facture téléchargé');
    } catch (err) {
      const msg = err.response?.status === 409
        ? 'Le devis n\'est pas encore signé. Téléversez d\'abord la signature.'
        : (err.response?.data?.detail || 'Erreur lors du téléchargement');
      toast.error(msg);
    }
  };

  // Récupère le fichier brut de signature manuscrite scannée (PDF / image).
  // Mode 'view' = ouvre un nouvel onglet. Mode 'download' = force le download.
  const fetchManualSignatureBlob = async (sim) => {
    const token = localStorage.getItem('token');
    const res = await axios.get(
      `${getSignatureBaseUrl(sim)}/manual-signature/download`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      },
    );
    const contentType = res.headers?.['content-type'] || res.data?.type || 'application/octet-stream';
    const disposition = res.headers?.['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : `signature_${sim.reference || sim.id}`;
    const blob = new Blob([res.data], { type: contentType });
    return { blob, filename, contentType };
  };

  const handleViewManualSignature = async (sim) => {
    try {
      const { blob } = await fetchManualSignatureBlob(sim);
      const url = URL.createObjectURL(blob);
      // Ouvrir dans un nouvel onglet pour visualisation rapide
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Veuillez autoriser les pop-ups pour visualiser le fichier');
      }
      // Libérer la blob URL après quelques secondes (le temps que le navigateur charge)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Aucune signature manuscrite téléversée pour ce devis.'
        : (err.response?.data?.detail || 'Erreur lors de l\'ouverture du fichier');
      toast.error(msg);
    }
  };

  const handleDownloadManualSignature = async (sim) => {
    try {
      const { blob, filename } = await fetchManualSignatureBlob(sim);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Signature manuscrite téléchargée');
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Aucune signature manuscrite téléversée pour ce devis.'
        : (err.response?.data?.detail || 'Erreur lors du téléchargement');
      toast.error(msg);
    }
  };

  // === Dossier Maître CP/CG (Feature 2026-05-07) ===
  // Téléchargement du Dossier Maître CP/CG NON SIGNÉ — uniquement DOMIUM.
  const handleDownloadDossierMaitre = async (sim) => {
    const isDomium = sim.type === 'simulation_domium' || sim.source === 'domium_subscriptions';
    if (!isDomium) {
      toast.error('Le Dossier Maître CP/CG n\'est disponible que pour les simulations DOS-DOMIUM.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const ref = encodeURIComponent(sim.reference || sim.id);
      const res = await axios.get(
        `${BACKEND_URL}/api/domium/documents/${ref}/dossier-maitre`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Dossier_Maitre_CP_CG_${sim.reference || sim.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Dossier Maître CP/CG téléchargé — à transmettre pour signature manuscrite');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du téléchargement du Dossier Maître');
    }
  };

  // Upload du retour CP/CG signés manuscritement — uniquement DOMIUM.
  const handleUploadCpcgSigned = (sim) => {
    const isDomium = sim.type === 'simulation_domium' || sim.source === 'domium_subscriptions';
    if (!isDomium) {
      toast.error('Le retour CP/CG signés n\'est traité que pour les simulations DOS-DOMIUM.');
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/png,image/jpeg,image/jpg';
    input.onchange = async (e) => {
      const file = e.target?.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10 Mo).');
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const ref = encodeURIComponent(sim.reference || sim.id);
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(
          `${BACKEND_URL}/api/domium/devis/${ref}/upload-cpcg-signed`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        toast.success(res.data?.message || 'CP/CG signés téléversés et archivés.');
        if (onRefresh) onRefresh();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erreur lors du téléversement');
      }
    };
    input.click();
  };

  // Récupère le fichier brut CP/CG signés (PDF / image).
  const fetchCpcgSignedBlob = async (sim) => {
    const token = localStorage.getItem('token');
    const ref = encodeURIComponent(sim.reference || sim.id);
    const res = await axios.get(
      `${BACKEND_URL}/api/domium/devis/${ref}/cpcg-signed/download`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      },
    );
    const contentType = res.headers?.['content-type'] || res.data?.type || 'application/octet-stream';
    const disposition = res.headers?.['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : `CP_CG_signes_${sim.reference || sim.id}`;
    const blob = new Blob([res.data], { type: contentType });
    return { blob, filename, contentType };
  };

  const handleViewCpcgSigned = async (sim) => {
    try {
      const { blob } = await fetchCpcgSignedBlob(sim);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Veuillez autoriser les pop-ups pour visualiser le fichier');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Aucun CP/CG signé téléversé pour ce devis.'
        : (err.response?.data?.detail || 'Erreur lors de l\'ouverture du fichier');
      toast.error(msg);
    }
  };

  const handleDownloadCpcgSigned = async (sim) => {
    try {
      const { blob, filename } = await fetchCpcgSignedBlob(sim);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CP/CG signés téléchargés');
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Aucun CP/CG signé téléversé pour ce devis.'
        : (err.response?.data?.detail || 'Erreur lors du téléchargement');
      toast.error(msg);
    }
  };

  if (!membreDocuments) return null;

  // Séparer les Propositions ESS (devis_ess, devis_ess_genere, proposition,
  // devis) des autres documents contractuels pour les afficher dans une
  // section dédiée premium au-dessus.
  const allContractuels = membreDocuments.documents_contractuels || [];
  const propositionsEss = allContractuels.filter(d => PROPOSITION_TYPES.has(d.type));
  const autresContractuels = allContractuels.filter(d => !PROPOSITION_TYPES.has(d.type));

  const archives = membreDocuments.documents_archives || {};
  const totalArchives = (archives.attestations_rcds?.length || 0)
    + (archives.attestations_ftpe?.length || 0)
    + (archives.conditions?.length || 0)
    + (archives.ged?.length || 0);

  const hasAny = membreDocuments.simulations_en_cours?.length ||
    membreDocuments.certificats_adhesion?.length ||
    membreDocuments.attestations_rcds_signees?.length ||
    membreDocuments.attestations_ftpe_signees?.length ||
    membreDocuments.conditions_signees?.length ||
    propositionsEss.length ||
    autresContractuels.length ||
    membreDocuments.documents_ged?.length ||
    membreDocuments.documents_admin?.length ||
    totalArchives;

  const hasSelectableItems = (membreDocuments.simulations_en_cours?.length || 0) > 0
    || propositionsEss.length > 0;

  return (
    <div className="space-y-6" data-testid="document-sections">
      <SoldesDusSection soldes={membreDocuments?.soldes_dus} />
      {/* Barre d'actions bulk : visible dès qu'un élément est sélectionné. */}
      {hasSelectableItems && selection.size > 0 && (
        <div
          className="sticky top-0 z-10 flex items-center justify-between gap-3 p-3 bg-red-50 border border-red-200 rounded-lg shadow-sm"
          data-testid="bulk-actions-bar"
        >
          <div className="flex items-center gap-2 text-sm text-red-900">
            <CheckCircle className="h-4 w-4 text-red-600" />
            <span className="font-semibold">
              {selection.size} élément{selection.size > 1 ? 's' : ''} sélectionné{selection.size > 1 ? 's' : ''}
            </span>
            <span className="text-xs text-red-700 hidden sm:inline">
              (simulations + propositions ESS)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              className="border-red-300 text-red-700 hover:bg-red-100"
              data-testid="bulk-clear-selection"
            >
              <X className="h-4 w-4 mr-1" />
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-button"
            >
              {bulkDeleting
                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <Trash2 className="h-4 w-4 mr-1" />}
              Supprimer la sélection
            </Button>
          </div>
        </div>
      )}

      <SimulationsSection
        items={membreDocuments.simulations_en_cours}
        onResume={onResume}
        selection={selection}
        onToggle={handleToggle}
        onDeleteOne={handleDeleteOne}
        onUploadDomiumSignature={handleUploadDomiumSignature}
        onDownloadDocumentFacture={handleDownloadDocumentFacture}
        onViewManualSignature={handleViewManualSignature}
        onDownloadManualSignature={handleDownloadManualSignature}
        onDownloadDossierMaitre={handleDownloadDossierMaitre}
        onUploadCpcgSigned={handleUploadCpcgSigned}
        onViewCpcgSigned={handleViewCpcgSigned}
        onDownloadCpcgSigned={handleDownloadCpcgSigned}
        onEditChantierInfo={handleEditChantierInfo}
        onManageIntervenants={handleManageIntervenants}
      />

      {/* Dialog d'édition des informations chantier (DOMIUM) */}
      <EditChantierInfoDialog
        open={!!editChantierSim}
        onOpenChange={(open) => { if (!open) setEditChantierSim(null); }}
        simulation={editChantierSim}
        onSaved={() => { onRefresh && onRefresh(); }}
      />

      {/* Dialog de gestion des intervenants (DOMIUM) */}
      <IntervenantsManagerDialog
        open={!!manageIntervenantsSim}
        onOpenChange={(open) => { if (!open) setManageIntervenantsSim(null); }}
        simulation={manageIntervenantsSim}
      />

      {/* Section premium dédiée aux Propositions ESS BATICO */}
      <PropositionsESSSection
        items={propositionsEss}
        membreId={membreId}
        selection={selection}
        onToggle={handleToggle}
        onDeleteOne={handleDeleteOne}
      />

      <GenericDocSection membreId={membreId} onRefresh={onRefresh} title="Devis LegalCoop" icon={FileText} iconColor="text-violet-500"
        items={membreDocuments.devis_legalcoop} bgColor="bg-violet-50" borderColor="border-violet-200" textColor="text-violet-700" />

      {membreDocuments.has_ftpe_signee && (
        <GenericDocSection membreId={membreId} onRefresh={onRefresh} title="Certificat d'Adhesion O'SCOP" icon={Award} iconColor="text-indigo-500"
          items={membreDocuments.certificats_adhesion} bgColor="bg-indigo-50" borderColor="border-indigo-200" textColor="text-indigo-700" />
      )}

      <DispositifsMembreSection membreId={membreId} membreDocuments={membreDocuments} />

      <GenericDocSection membreId={membreId} onRefresh={onRefresh} title="Conditions générales / particulières signées" icon={FileCheck} iconColor="text-emerald-500"
        items={(membreDocuments.conditions_signees || []).filter((x) => !x.souscription_id)} bgColor="bg-emerald-50" borderColor="border-emerald-200" textColor="text-emerald-700" />

      <FtpeSection items={membreDocuments.attestations_ftpe_signees} membreId={membreId} onRefresh={onRefresh} />

      <GedSection items={membreDocuments.documents_ged} membreId={membreId} onRefresh={onRefresh} />

      <AdminDocsSection items={membreDocuments.documents_admin} />

      {/* Section "Archivés" dépliable — contrats échus (date_echeance + 1 jour < now) */}
      <ArchivedDocumentsSection archives={membreDocuments.documents_archives} />

      {!hasAny && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>Aucun document ou simulation pour ce membre</p>
        </div>
      )}
    </div>
  );
}

export { GenericDocSection, SimulationsSection, FtpeSection, GedSection, AdminDocsSection, PropositionsESSSection, getFileIcon, formatFileSize, getCategorieLabel };
